import { Injectable, effect, signal, computed, inject } from '@angular/core';
import { ScreenSize, ScreenSizeService } from '@/app/design-system/utils/screen-size.service';

export interface LayoutConfig {
  preset: string;
  primary: string;
  surface: string | undefined | null;
  darkTheme: boolean;
  menuMode: string;
}

interface LayoutState {
  staticMenuDesktopInactive: boolean;
  overlayMenuActive: boolean;
  configSidebarVisible: boolean;
  mobileMenuActive: boolean;
  menuHoverActive: boolean;
  activePath: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class LayoutService {
  private readonly screenSize = inject(ScreenSizeService);

  layoutConfig = signal<LayoutConfig>({
    preset: 'Aura',
    primary: 'emerald',
    surface: null,
    darkTheme: false,
    menuMode: 'static'
  });

  layoutState = signal<LayoutState>({
    staticMenuDesktopInactive: false,
    overlayMenuActive: false,
    configSidebarVisible: false,
    mobileMenuActive: false,
    menuHoverActive: false,
    activePath: null
  });

  theme = computed(() => (this.layoutConfig().darkTheme ? 'dark' : 'light'));

  isSidebarActive = computed(() => this.layoutState().overlayMenuActive || this.layoutState().mobileMenuActive);

  isDarkTheme = computed(() => this.layoutConfig().darkTheme);

  getPrimary = computed(() => this.layoutConfig().primary);

  getSurface = computed(() => this.layoutConfig().surface);

  isOverlay = computed(() => this.layoutConfig().menuMode === 'overlay');

  transitionComplete = signal<boolean>(false);

  private static readonly DARK_THEME_KEY = 'sokil-crm-dark-theme';
  private initialized = false;

  constructor() {
    this.restoreTheme();

    effect(() => {
      const config = this.layoutConfig();

      if (!this.initialized || !config) {
        this.initialized = true;
        return;
      }

      localStorage.setItem(LayoutService.DARK_THEME_KEY, String(config.darkTheme));
      this.handleDarkModeTransition(config);
    });

    // Auto-collapse sidebar on compact desktops, expand on full desktops
    effect(() => {
      const size = this.screenSize.size();
      if (this.layoutConfig().menuMode !== 'static') return;

      if (size === ScreenSize.Compact) {
        this.layoutState.update((prev) => ({ ...prev, staticMenuDesktopInactive: true }));
      } else if (size === ScreenSize.Full) {
        this.layoutState.update((prev) => ({ ...prev, staticMenuDesktopInactive: false }));
      }
    });
  }

  private restoreTheme(): void {
    const saved = localStorage.getItem(LayoutService.DARK_THEME_KEY);
    if (saved === null) return;

    const isDark = saved === 'true';
    this.layoutConfig.update(c => ({ ...c, darkTheme: isDark }));
    if (isDark) {
      document.documentElement.classList.add('app-dark');
    }
  }

  private handleDarkModeTransition(config: LayoutConfig): void {
    const supportsViewTransition = 'startViewTransition' in document;

    if (supportsViewTransition) {
      this.startViewTransition(config);
    } else {
      this.toggleDarkMode(config);
    }
  }

  private startViewTransition(config: LayoutConfig): void {
    (document as any).startViewTransition(() => {
      this.toggleDarkMode(config);
    });
  }

  toggleDarkMode(config?: LayoutConfig): void {
    const _config = config || this.layoutConfig();
    if (_config.darkTheme) {
      document.documentElement.classList.add('app-dark');
    } else {
      document.documentElement.classList.remove('app-dark');
    }
  }

  onMenuToggle() {
    if (this.isOverlay()) {
      this.layoutState.update((prev) => ({ ...prev, overlayMenuActive: !prev.overlayMenuActive }));
    } else if (this.isDesktop()) {
      this.layoutState.update((prev) => ({ ...prev, staticMenuDesktopInactive: !prev.staticMenuDesktopInactive }));
    } else {
      this.layoutState.update((prev) => ({ ...prev, mobileMenuActive: !prev.mobileMenuActive }));
    }
  }

  showConfigSidebar() {
    this.layoutState.update((prev) => ({ ...prev, configSidebarVisible: true }));
  }

  hideConfigSidebar() {
    this.layoutState.update((prev) => ({ ...prev, configSidebarVisible: false }));
  }

  isDesktop() {
    return this.screenSize.isDesktop();
  }

  isMobile() {
    return this.screenSize.isMobile();
  }
}
